import { produce } from 'immer';
import { ItemTypes } from '../constants/constants';
import { isATableBox, isATitleBox } from '../utils/boxes';

const stripDiacritics = (str) =>
  str
    ? str
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
    : str;

const buildHaystackPositionMap = (text) => {
  const map = [];
  let h = 0;
  for (let i = 0; i < text.length; ) {
    const cp = text.codePointAt(i);
    const charStr = String.fromCodePoint(cp);
    const strippedLen = stripDiacritics(charStr).length;
    for (let j = 0; j < strippedLen; j++) {
      map[h + j] = i;
    }
    h += strippedLen;
    i += charStr.length;
  }
  map[h] = text.length;
  return map;
};

const mapHaystackRangeToOriginal = (text, hStart, hEnd) => {
  const map = buildHaystackPositionMap(text);
  return { start: map[hStart] ?? 0, end: map[hEnd] ?? text.length };
};

const resolveReplaceOffsets = (blockText, start, end, ignoreAccents) => {
  if (!ignoreAccents) return { start, end };
  return mapHaystackRangeToOriginal(blockText, start, end);
};

const createEditorStateWithDecorator = (content) => {
  const { EditorState } = require('draft-js');
  const { decorator } = require('../components/RichEditor/utils');
  return EditorState.createWithContent(content, decorator);
};

const isTextBox = (type) =>
  type === ItemTypes.TEXT ||
  type === ItemTypes.TEXT_VARIABLE ||
  type === 'text' ||
  type === 'text_variable';

const buildRegex = ({ query, matchCase, wholeWord }) => {
  if (!query) return null;
  const ignoreAccents = !matchCase;
  // Échapper le terme
  const baseQuery = ignoreAccents ? stripDiacritics(query) : query;
  let escaped = baseQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Gérer les apostrophes et guillemets (droits et typographiques)
  // Apostrophes: ' (U+0027), ’ (U+2019)
  escaped = escaped.replace(/[\u2019']/g, "(?:'|\u2019)");
  // Guillemets doubles/citadins: " (U+0022), “ (U+201C), ” (U+201D), « (U+00AB), » (U+00BB), ‹ (U+2039), › (U+203A)
  escaped = escaped.replace(
    /["\u201C\u201D\u00AB\u00BB\u2039\u203A]/g,
    '(?:"|\u201C|\u201D|\u00AB|\u00BB|\u2039|\u203A)'
  );
  const pattern = wholeWord ? `(?<!\\w)${escaped}(?!\\w)` : escaped;
  const flags = matchCase ? 'gu' : 'giu';
  try {
    const regex = new RegExp(pattern, flags);
    regex._ignoreAccents = ignoreAccents;
    return regex;
  } catch (e) {
    // Fallback sans lookbehind si non supporté
    const fb = wholeWord ? `(?:^|[^\\w])(${escaped})(?=[^\\w]|$)` : escaped;
    const regex = new RegExp(fb, flags);
    regex._ignoreAccents = ignoreAccents;
    return regex;
  }
};

const finalizeDocumentReplace = (get, affectedBoxIds) => {
  const uniqueIds = [...new Set(affectedBoxIds.filter(Boolean))];
  uniqueIds.forEach((id) => get().addSkipBoxSaveOnUnselect(id));
  get().bumpDocumentContentSyncTick();
  try {
    const selectedBoxId = get().selectedBoxId;
    if (selectedBoxId && uniqueIds.includes(selectedBoxId)) {
      get().handleUnSelectBox('search_replace');
    }
  } catch (e) {}
};

export const searchActions = (set, get) => ({
  searchQuery: '',
  searchMatchCase: false,
  searchWholeWord: false,
  searchResults: [], // [{ boxId, blockKey, start, end }]
  searchIndex: -1,
  searchScope: 'document', // 'document' | 'variables'
  searchUiHasFocus: false,
  searchSelectionActive: false,
  ignoreVariableSaveKeys: [],
  searchScrollToBoxId: null,
  skipBoxSaveOnUnselectIds: [],
  documentContentSyncTick: 0,

  addSkipBoxSaveOnUnselect: (boxId) => {
    if (!boxId) return;
    set(
      produce((draft) => {
        if (!draft.skipBoxSaveOnUnselectIds.includes(boxId)) {
          draft.skipBoxSaveOnUnselectIds.push(boxId);
        }
      }),
      false,
      'addSkipBoxSaveOnUnselect'
    );
  },

  consumeSkipBoxSaveOnUnselect: (boxId) => {
    if (!boxId) return false;
    const shouldSkip = get().skipBoxSaveOnUnselectIds.includes(boxId);
    if (shouldSkip) {
      set(
        produce((draft) => {
          draft.skipBoxSaveOnUnselectIds = draft.skipBoxSaveOnUnselectIds.filter(
            (id) => id !== boxId
          );
        }),
        false,
        'consumeSkipBoxSaveOnUnselect'
      );
    }
    return shouldSkip;
  },

  bumpDocumentContentSyncTick: () => {
    set(
      (state) => ({
        documentContentSyncTick: (state.documentContentSyncTick || 0) + 1,
      }),
      false,
      'bumpDocumentContentSyncTick'
    );
  },

  setSearchParams: ({ query, matchCase, wholeWord }) => {
    set(
      produce((draft) => {
        if (query !== undefined) draft.searchQuery = query;
        if (matchCase !== undefined) draft.searchMatchCase = matchCase;
        if (wholeWord !== undefined) draft.searchWholeWord = wholeWord;
      }),
      false,
      'setSearchParams'
    );
  },

  setSearchScope: (scope) => {
    set(
      { searchScope: scope, searchResults: [], searchIndex: -1 },
      false,
      'setSearchScope'
    );
  },

  setSearchUiFocus: (hasFocus) => {
    set({ searchUiHasFocus: !!hasFocus }, false, 'setSearchUiFocus');
  },

  setSearchSelectionActive: (active) => {
    if (!!active === get().searchSelectionActive) return;
    set({ searchSelectionActive: !!active }, false, 'setSearchSelectionActive');
  },

  addIgnoreVariableSaveKey: (varKey) => {
    set(
      produce((draft) => {
        if (!draft.ignoreVariableSaveKeys.includes(varKey))
          draft.ignoreVariableSaveKeys.push(varKey);
      }),
      false,
      'addIgnoreVariableSaveKey'
    );
  },
  removeIgnoreVariableSaveKey: (varKey) => {
    set(
      produce((draft) => {
        draft.ignoreVariableSaveKeys = draft.ignoreVariableSaveKeys.filter(
          (k) => k !== varKey
        );
      }),
      false,
      'removeIgnoreVariableSaveKey'
    );
  },
  clearIgnoreVariableSaveKeys: () => {
    set({ ignoreVariableSaveKeys: [] }, false, 'clearIgnoreVariableSaveKeys');
  },

  requestScrollToBox: (boxId) => {
    set({ searchScrollToBoxId: boxId }, false, 'requestScrollToBox');
  },
  consumeScrollToBox: () => {
    set({ searchScrollToBoxId: null }, false, 'consumeScrollToBox');
  },

  computeSearchResults: () => {
    const state = get();
    const {
      boxes,
      masks,
      searchQuery,
      searchMatchCase,
      searchWholeWord,
      variables,
    } = state;
    // Utiliser le scope fixé par l'UI
    const currentScope = state?.searchScope || 'document';
    console.log('🔍 Recherche:', {
      searchQuery,
      searchMatchCase,
      searchWholeWord,
      currentScope,
    });
    const regex = buildRegex({
      query: searchQuery,
      matchCase: searchMatchCase,
      wholeWord: searchWholeWord,
    });
    console.log('🔍 Regex construite:', regex);
    const results = [];
    if (!regex) {
      set({ searchResults: [], searchIndex: -1 }, false, 'clearSearchResults');
      return;
    }
    const allBoxes = [...boxes, ...masks.map((m) => m.boxes).flat()];
    console.log('🔍 Total boxes à analyser:', allBoxes.length);
    let textBoxesCount = 0;
    let titleBoxesCount = 0;
    let tableBoxesCount = 0;
    if (currentScope === 'document') {
      // Recherche dans les boxes de texte
      allBoxes.forEach((box) => {
        if (!isTextBox(box.type) || !box?.content?.editorState) return;
        // Exclure totalement les boxes de type text_variable du mode document
        if (
          box.type === ItemTypes.TEXT_VARIABLE ||
          box.type === 'text_variable'
        )
          return;
        textBoxesCount++;
        const content = box.content.editorState.getCurrentContent();
        content.getBlocksAsArray().forEach((block) => {
          const text = block.getText();
          const haystack = regex._ignoreAccents ? stripDiacritics(text) : text;
          // Exclure les occurrences situées dans des entités VARIABLE
          const excludedRanges = [];
          block.findEntityRanges(
            (character) => {
              const entityKey = character.getEntity();
              if (entityKey === null) return false;
              try {
                return content.getEntity(entityKey).getType() === 'VARIABLE';
              } catch (e) {
                return false;
              }
            },
            (start, end) => {
              excludedRanges.push({ start, end });
            }
          );
          const isInsideExcluded = (start, end) =>
            excludedRanges.some((r) => start >= r.start && end <= r.end);

          let m;
          regex.lastIndex = 0;
          while ((m = regex.exec(haystack)) !== null) {
            const s = m.index;
            const e = m.index + m[0].length;
            if (!isInsideExcluded(s, e)) {
              results.push({
                boxId: box.id,
                blockKey: block.getKey(),
                start: s,
                end: e,
              });
            }
            if (m.index === regex.lastIndex) regex.lastIndex++; // safety for zero-width
          }
        });
      });

      // Recherche dans les blocs titre
      allBoxes.forEach((box) => {
        if (!isATitleBox(box.type) || !box?.content?.editorState) return;
        titleBoxesCount++;
        const content = box.content.editorState.getCurrentContent();
        content.getBlocksAsArray().forEach((block) => {
          const text = block.getText();
          const haystack = regex._ignoreAccents ? stripDiacritics(text) : text;
          // Exclure les occurrences situées dans des entités VARIABLE
          const excludedRanges = [];
          block.findEntityRanges(
            (character) => {
              const entityKey = character.getEntity();
              if (entityKey === null) return false;
              try {
                return content.getEntity(entityKey).getType() === 'VARIABLE';
              } catch (e) {
                return false;
              }
            },
            (start, end) => {
              excludedRanges.push({ start, end });
            }
          );
          const isInsideExcluded = (start, end) =>
            excludedRanges.some((r) => start >= r.start && end <= r.end);

          let m;
          regex.lastIndex = 0;
          while ((m = regex.exec(haystack)) !== null) {
            const s = m.index;
            const e = m.index + m[0].length;
            if (!isInsideExcluded(s, e)) {
              results.push({
                boxId: box.id,
                blockKey: block.getKey(),
                start: s,
                end: e,
              });
            }
            if (m.index === regex.lastIndex) regex.lastIndex++; // safety for zero-width
          }
        });
      });

      // Recherche dans les tableaux
      allBoxes.forEach((box) => {
        if (!isATableBox(box.type) || !box?.content?.columns) return;
        tableBoxesCount++;
        try {
          const { transformTextToEditorState } = require('../utils/dataSync');
          const { convertContentToRawText } = require('../components/RichEditor/utils');

          // Recherche dans les en-têtes (headers) des colonnes
          box.content.columns.forEach((column) => {
            if (!column || !column.key) return;
            const columnKey = column.key;
            const headerTitle = column.title;

            if (!headerTitle) return;

            // Convertir le titre de l'en-tête en EditorState puis en texte
            try {
              const editorState = transformTextToEditorState(headerTitle);
              const content = editorState.getCurrentContent();

              content.getBlocksAsArray().forEach((block) => {
                const blockText = block.getText();
                const haystack = regex._ignoreAccents
                  ? stripDiacritics(blockText)
                  : blockText;

                // Exclure les occurrences situées dans des entités VARIABLE
                const excludedRanges = [];
                block.findEntityRanges(
                  (character) => {
                    const entityKey = character.getEntity();
                    if (entityKey === null) return false;
                    try {
                      return content.getEntity(entityKey).getType() === 'VARIABLE';
                    } catch (e) {
                      return false;
                    }
                  },
                  (start, end) => {
                    excludedRanges.push({ start, end });
                  }
                );
                const isInsideExcluded = (start, end) =>
                  excludedRanges.some((r) => start >= r.start && end <= r.end);

                let m;
                regex.lastIndex = 0;
                while ((m = regex.exec(haystack)) !== null) {
                  const s = m.index;
                  const e = m.index + m[0].length;
                  if (!isInsideExcluded(s, e)) {
                    results.push({
                      boxId: box.id,
                      rowKey: null, // null pour les headers
                      columnKey: columnKey,
                      blockKey: block.getKey(),
                      start: s,
                      end: e,
                      isTable: true,
                      isHeader: true, // Indicateur pour les headers
                    });
                  }
                  if (m.index === regex.lastIndex) regex.lastIndex++; // safety for zero-width
                }
              });
            } catch (e) {
              console.warn('Erreur lors de la recherche dans l\'en-tête:', e);
            }
          });

          // Recherche dans les lignes de données du tableau
          if (box?.content?.data) {
            box.content.data.forEach((row) => {
              if (!row || !row.key) return;
              const rowKey = row.key;

              // Parcourir toutes les colonnes
              box.content.columns.forEach((column) => {
                if (!column || !column.key) return;
                const columnKey = column.key;
                const cellContent = row[columnKey];

                if (!cellContent) return;

                // Convertir le contenu de la cellule en EditorState puis en texte
                try {
                  const editorState = transformTextToEditorState(cellContent);
                  const text = convertContentToRawText(editorState);

                  if (!text) return;

                  // Chercher dans le texte de la cellule
                  const content = editorState.getCurrentContent();
                  content.getBlocksAsArray().forEach((block) => {
                    const blockText = block.getText();
                    const haystack = regex._ignoreAccents
                      ? stripDiacritics(blockText)
                      : blockText;

                    // Exclure les occurrences situées dans des entités VARIABLE
                    const excludedRanges = [];
                    block.findEntityRanges(
                      (character) => {
                        const entityKey = character.getEntity();
                        if (entityKey === null) return false;
                        try {
                          return content.getEntity(entityKey).getType() === 'VARIABLE';
                        } catch (e) {
                          return false;
                        }
                      },
                      (start, end) => {
                        excludedRanges.push({ start, end });
                      }
                    );
                    const isInsideExcluded = (start, end) =>
                      excludedRanges.some((r) => start >= r.start && end <= r.end);

                    let m;
                    regex.lastIndex = 0;
                    while ((m = regex.exec(haystack)) !== null) {
                      const s = m.index;
                      const e = m.index + m[0].length;
                      if (!isInsideExcluded(s, e)) {
                        results.push({
                          boxId: box.id,
                          rowKey: rowKey,
                          columnKey: columnKey,
                          blockKey: block.getKey(),
                          start: s,
                          end: e,
                          isTable: true,
                          isHeader: false, // Indicateur pour les cellules de données
                        });
                      }
                      if (m.index === regex.lastIndex) regex.lastIndex++; // safety for zero-width
                    }
                  });
                } catch (e) {
                  console.warn('Erreur lors de la recherche dans la cellule:', e);
                }
              });
            });
          }
        } catch (e) {
          console.warn('Erreur lors de la recherche dans le tableau:', e);
        }
      });
    } else {
      // variables scope: s'aligner sur ce que le formulaire affiche réellement
      // On utilise le groupement utilitaire afin d'obtenir les variables visibles
      const { variableGroups, columns, containers, rowContainers } = state;
      try {
        const grouped =
          require('../utils/variables').getVariablesGroupedByContainer({
            variableGroups,
            variables,
            boxes,
            containers,
            columns,
            rowContainers,
          });
        const visibleVariableIds = new Set();
        grouped.forEach((vInfos) => {
          (vInfos.variablesBoxes || []).forEach((vb) => {
            if (vb?.hideField || vb?.notUsed) return;
            // Adresse découpée : le formulaire n'affiche que ses parties. Les
            // remplacer recompose l'agrégat ; remplacer l'agrégat seul serait
            // perdu à la saisie suivante d'une partie.
            (vb.addressParts ?? [vb]).forEach(({ id }) => visibleVariableIds.add(id));
          });
        });
        console.log('🔍 Variables visibles (form):', visibleVariableIds.size);
        const allBoxesMap = new Map(allBoxes.map((b) => [b.id, b]));
        const getFallbackFromRaw = (raw) => {
          try {
            const obj = JSON.parse(raw || '{}');
            const blocks = obj?.blocks || [];
            return blocks.map((b) => b.text || '').join('\n');
          } catch (e) {
            return '';
          }
        };
        visibleVariableIds.forEach((varKey) => {
          let value = (variables?.[varKey]?.value ?? '').toString();
          if (!value) {
            const box = allBoxesMap.get(varKey);
            if (
              box &&
              (box.type === ItemTypes.TEXT_VARIABLE ||
                box.type === 'text_variable')
            ) {
              // essayer defaultValue.content.raw (ou content.raw) comme fallback
              value =
                getFallbackFromRaw(box?.content?.raw) ||
                getFallbackFromRaw(box?.defaultValue?.content?.raw) ||
                '';
            }
          }
          let m;
          const haystack = regex._ignoreAccents
            ? stripDiacritics(value)
            : value;
          regex.lastIndex = 0;
          while ((m = regex.exec(haystack)) !== null) {
            // start/end exacts dans la value pour pouvoir sélectionner dans le textarea
            results.push({
              boxId: varKey,
              blockKey: null,
              start: m.index,
              end: m.index + m[0].length,
            });
            if (m.index === regex.lastIndex) regex.lastIndex++;
          }
        });
      } catch (e) {
        console.warn('Search variables scope failed to group:', e);
      }
    }
    console.log('🔍 Boxes de texte analysées:', textBoxesCount);
    console.log('🔍 Blocs titre analysés:', titleBoxesCount);
    console.log('🔍 Tableaux analysés:', tableBoxesCount);
    console.log('🔍 Résultats finaux:', results.length);
    // Filtrer pour ne garder que les résultats visibles
    const containersList = get().containers;
    const visibleResults = results.filter((r) => {
      if (currentScope === 'variables') return true; // déjà filtré
      const box = allBoxes.find((b) => b.id === r.boxId);
      if (!box) return false;
      return containersList.some((c) => c.columnsIds.includes(box.columnId));
    });
    console.log('🔍 Résultats visibles:', visibleResults.length);

    // Trier les résultats selon leur position dans le document
    const sortedResults = visibleResults.sort((a, b) => {
      const boxA = allBoxes.find((box) => box.id === a.boxId);
      const boxB = allBoxes.find((box) => box.id === b.boxId);

      if (!boxA || !boxB) return 0;

      // 1. Trier par container (ordre des containers dans le document)
      // Trouver le container qui contient chaque box
      const containerA = containersList.find((c) => c.columnsIds.includes(boxA.columnId));
      const containerB = containersList.find((c) => c.columnsIds.includes(boxB.columnId));

      if (containerA && containerB) {
        const containerAIndex = containersList.indexOf(containerA);
        const containerBIndex = containersList.indexOf(containerB);

        if (containerAIndex !== containerBIndex) {
          return containerAIndex - containerBIndex;
        }
      } else if (containerA && !containerB) {
        return -1; // A avant B si A a un container et B non
      } else if (!containerA && containerB) {
        return 1; // A après B si A n'a pas de container et B oui
      }

      // 2. Si même container, trier par colonne dans le container
      if (containerA && containerB && containerA === containerB) {
        const columnAIndex = containerA.columnsIds.indexOf(boxA.columnId);
        const columnBIndex = containerB.columnsIds.indexOf(boxB.columnId);

        if (columnAIndex !== columnBIndex) {
          return columnAIndex - columnBIndex;
        }
      } else {
        // Si pas de container ou containers différents, utiliser l'ordre global des colonnes
        const columnsIdOrdered = containersList.map((cont) => cont.columnsIds).flat();
        const columnAIndex = columnsIdOrdered.indexOf(boxA.columnId);
        const columnBIndex = columnsIdOrdered.indexOf(boxB.columnId);

        if (columnAIndex !== columnBIndex) {
          return columnAIndex - columnBIndex;
        }
      }

      // 2. Si même colonne, trier par position verticale (top)
      const topA = boxA.groupTop ?? boxA.top ?? 0;
      const topB = boxB.groupTop ?? boxB.top ?? 0;

      if (topA !== topB) {
        return topA - topB;
      }

      // 4. Si même box (tableau), trier par position dans le tableau
      if (a.isTable && b.isTable && a.boxId === b.boxId) {
        // Les headers viennent avant les cellules
        if (a.isHeader && !b.isHeader) return -1;
        if (!a.isHeader && b.isHeader) return 1;

        // Si tous les deux sont des headers ou des cellules, trier par columnKey puis rowKey
        if (a.columnKey !== b.columnKey) {
          // Trouver l'ordre des colonnes dans le tableau
          const tableBox = boxA;
          if (tableBox.content?.columns) {
            const colAIndex = tableBox.content.columns.findIndex((col) => col.key === a.columnKey);
            const colBIndex = tableBox.content.columns.findIndex((col) => col.key === b.columnKey);
            if (colAIndex !== -1 && colBIndex !== -1 && colAIndex !== colBIndex) {
              return colAIndex - colBIndex;
            }
          }
        }

        // Si même colonne, trier par rowKey (pour les cellules)
        if (!a.isHeader && !b.isHeader && a.rowKey && b.rowKey) {
          const tableBox = boxA;
          if (tableBox.content?.data) {
            const rowAIndex = tableBox.content.data.findIndex((row) => row.key === a.rowKey);
            const rowBIndex = tableBox.content.data.findIndex((row) => row.key === b.rowKey);
            if (rowAIndex !== -1 && rowBIndex !== -1 && rowAIndex !== rowBIndex) {
              return rowAIndex - rowBIndex;
            }
          }
        }

        // Si même cellule/header, trier par position dans le texte (start)
        return a.start - b.start;
      }

      // 5. Si même box (texte), trier par blockKey puis par position dans le texte
      if (a.blockKey !== b.blockKey) {
        // Pour les blocks, on peut utiliser l'ordre alphabétique ou l'ordre dans le contenu
        // Ici on utilise simplement l'ordre alphabétique des blockKeys
        return a.blockKey.localeCompare(b.blockKey);
      }

      // 5. Même block, trier par position dans le texte
      return a.start - b.start;
    });

    console.log('🔍 Résultats triés:', sortedResults.length);
    set(
      {
        searchResults: sortedResults,
        searchIndex: sortedResults.length > 0 ? 0 : -1,
      },
      false,
      'computeSearchResults'
    );
  },

  nextSearchResult: () => {
    const { searchResults, searchIndex } = get();
    if (searchResults.length === 0) return null;
    const newIndex = (searchIndex + 1) % searchResults.length;
    set({ searchIndex: newIndex }, false, 'nextSearchResult');
    return searchResults[newIndex];
  },

  prevSearchResult: () => {
    const { searchResults, searchIndex } = get();
    if (searchResults.length === 0) return null;
    const newIndex =
      (searchIndex - 1 + searchResults.length) % searchResults.length;
    set({ searchIndex: newIndex }, false, 'prevSearchResult');
    return searchResults[newIndex];
  },

  clearSearchResults: () => {
    set(
      { searchResults: [], searchIndex: -1 },
      false,
      'clearSearchResults_manual'
    );
  },

  setSearchIndex: (newIndex) => {
    set({ searchIndex: newIndex }, false, 'setSearchIndex');
  },

  replaceActiveOccurrence: (replacement) => {
    const state = get();
    const { searchResults, searchIndex } = state;
    const current = searchResults[searchIndex];
    if (!current) return;
    const { boxId, blockKey, start, end } = current;
    // variables scope: replace in variables value
    if (
      state.searchScope === 'variables' ||
      state.overlayProps?.left?.shownStatus === 'variables'
    ) {
      set(
        produce((draft) => {
          if (draft.variables[boxId]) {
            const val = String(draft.variables[boxId].value ?? '');
            const newVal = val.slice(0, start) + replacement + val.slice(end);
            draft.variables[boxId].value = newVal;
            // Injecter la valeur dans les boxes liées pour mise à jour UI
            try {
              const {
                mutateInjectVariablesInDocument,
              } = require('../utils/variables');
              mutateInjectVariablesInDocument({
                variables: draft.variables,
                variable: draft.variables[boxId],
                variableKey: boxId,
                draft,
              });
            } catch (e) {}
          }
        }),
        false,
        'replaceActiveOccurrence:variables'
      );
      // Empêcher le onBlur/save de réécrire l'ancienne valeur
      get().addIgnoreVariableSaveKey(boxId);
      // recompute search to refresh ranges
      get().computeSearchResults();
      // désactiver la sélection bloquante
      set(
        { searchSelectionActive: false },
        false,
        'replaceActiveOccurrence:variables:done'
      );
      return;
    }
    // document scope: replace in Draft.js editorState preserving styles
    {
      const ignoreAccents = !state.searchMatchCase;
      const allBoxes = [
        ...state.boxes,
        ...state.masks.map((m) => m.boxes).flat(),
      ];
      const box = allBoxes.find((b) => b.id === boxId);

      // Gérer le remplacement dans les tableaux
      if (current.isTable && box && isATableBox(box.type) && current.columnKey) {
        try {
          const Modifier = require('draft-js').Modifier;
          const SelectionState = require('draft-js').SelectionState;
          const { transformTextToEditorState } = require('../utils/dataSync');
          const { convertToRaw } = require('draft-js');

          // Gérer le remplacement dans les en-têtes (headers)
          if (current.isHeader) {
            const column = box.content.columns.find((col) => col.key === current.columnKey);
            if (column && column.title) {
              const headerTitle = column.title;
              const editorState = transformTextToEditorState(headerTitle);
              const content = editorState.getCurrentContent();
              const blockText = content.getBlockForKey(blockKey).getText();
              const { start: origStart, end: origEnd } = resolveReplaceOffsets(
                blockText,
                start,
                end,
                ignoreAccents
              );

              const selection = SelectionState.createEmpty(blockKey).merge({
                anchorOffset: origStart,
                focusOffset: origEnd,
              });

              const newContent = Modifier.replaceText(
                content,
                selection,
                replacement,
                content.getBlockForKey(blockKey).getInlineStyleAt(origStart)
              );

              // Sauvegarder le nouveau titre dans l'en-tête
              get().updateBox(boxId, (b) => {
                const columnToUpdate = b.content.columns.find((col) => col.key === current.columnKey);
                if (columnToUpdate) {
                  columnToUpdate.title = JSON.stringify(
                    convertToRaw(newContent)
                  );
                }
              });
            }
          }
          // Gérer le remplacement dans les cellules de données
          else if (current.rowKey) {
            const row = box.content.data.find((r) => r.key === current.rowKey);
            if (row && row[current.columnKey]) {
              const cellContent = row[current.columnKey];
              const editorState = transformTextToEditorState(cellContent);
              const content = editorState.getCurrentContent();
              const blockText = content.getBlockForKey(blockKey).getText();
              const { start: origStart, end: origEnd } = resolveReplaceOffsets(
                blockText,
                start,
                end,
                ignoreAccents
              );

              const selection = SelectionState.createEmpty(blockKey).merge({
                anchorOffset: origStart,
                focusOffset: origEnd,
              });

              const newContent = Modifier.replaceText(
                content,
                selection,
                replacement,
                content.getBlockForKey(blockKey).getInlineStyleAt(origStart)
              );

              // Sauvegarder le nouveau contenu dans la cellule
              get().updateBox(boxId, (b) => {
                const rowToUpdate = b.content.data.find((r) => r.key === current.rowKey);
                if (rowToUpdate) {
                  rowToUpdate[current.columnKey] = JSON.stringify(
                    convertToRaw(newContent)
                  );
                }
              });
            }
          }
        } catch (e) {
          console.warn('Erreur lors du remplacement dans le tableau:', e);
        }
      } else if (box?.content?.editorState) {
        // Gérer le remplacement dans les boxes de texte et titres
        const Modifier = require('draft-js').Modifier;
        const SelectionState = require('draft-js').SelectionState;
        const content = box.content.editorState.getCurrentContent();
        const blockText = content.getBlockForKey(blockKey).getText();
        const { start: origStart, end: origEnd } = resolveReplaceOffsets(
          blockText,
          start,
          end,
          ignoreAccents
        );
        const selection = SelectionState.createEmpty(blockKey).merge({
          anchorOffset: origStart,
          focusOffset: origEnd,
        });
        const newContent = Modifier.replaceText(
          content,
          selection,
          replacement,
          content.getBlockForKey(blockKey).getInlineStyleAt(origStart)
        );
        get().updateBox(boxId, (b) => {
          b.content.editorState = createEditorStateWithDecorator(newContent);
        });
      }
      finalizeDocumentReplace(get, [boxId]);
    }
    get().computeSearchResults();
    set(
      { searchSelectionActive: false },
      false,
      'replaceActiveOccurrence:document:done'
    );
  },

  replaceAllOccurrences: (replacement) => {
    const state = get();
    const { searchResults } = state;
    if (searchResults.length === 0) return;
    if (
      state.searchScope === 'variables' ||
      state.overlayProps?.left?.shownStatus === 'variables'
    ) {
      set(
        produce((draft) => {
          // group by variable key
          const byVar = {};
          searchResults.forEach((r) => {
            (byVar[r.boxId] = byVar[r.boxId] || []).push(r);
          });
          Object.entries(byVar).forEach(([varKey, ranges]) => {
            if (!draft.variables[varKey]) return;
            let text = String(draft.variables[varKey].value ?? '');
            // replace from right to left to preserve indices
            ranges
              .sort((a, b) => b.start - a.start)
              .forEach(({ start, end }) => {
                text = text.slice(0, start) + replacement + text.slice(end);
              });
            draft.variables[varKey].value = text;
            // Inject into document boxes for UI refresh
            try {
              const {
                mutateInjectVariablesInDocument,
              } = require('../utils/variables');
              mutateInjectVariablesInDocument({
                variables: draft.variables,
                variable: draft.variables[varKey],
                variableKey: varKey,
                draft,
              });
            } catch (e) {}
            // Protéger contre un onBlur/save qui écrase
            if (varKey) get().addIgnoreVariableSaveKey(varKey);
          });
        }),
        false,
        'replaceAll:variables'
      );
      get().computeSearchResults();
      set({ searchSelectionActive: false }, false, 'replaceAll:variables:done');
      return;
    }
    // document scope
    {
      const ignoreAccents = !state.searchMatchCase;
      const Modifier = require('draft-js').Modifier;
      const SelectionState = require('draft-js').SelectionState;
      const { transformTextToEditorState } = require('../utils/dataSync');
      const { convertToRaw } = require('draft-js');
      const allBoxes = [
        ...state.boxes,
        ...state.masks.map((m) => m.boxes).flat(),
      ];
      const affectedBoxIds = [];

      // Séparer les résultats de tableaux et les résultats de texte
      const tableResults = searchResults.filter((r) => r.isTable);
      const textResults = searchResults.filter((r) => !r.isTable);

      // Séparer les résultats de headers et de cellules
      const headerResults = tableResults.filter((r) => r.isHeader);
      const cellResults = tableResults.filter((r) => !r.isHeader);

      // Traiter les en-têtes (headers) des tableaux
      const byTableHeader = {};
      headerResults.forEach((r) => {
        const key = `${r.boxId}:${r.columnKey}`;
        (byTableHeader[key] = byTableHeader[key] || []).push(r);
      });

      Object.entries(byTableHeader).forEach(([key, ranges]) => {
        const [boxId, columnKey] = key.split(':');
        const box = allBoxes.find((b) => b.id === boxId);
        if (!box || !isATableBox(box.type)) return;

        const column = box.content.columns.find((col) => col.key === columnKey);
        if (!column || !column.title) return;

        try {
          const headerTitle = column.title;
          let editorState = transformTextToEditorState(headerTitle);
          let newContent = editorState.getCurrentContent();

          const byBlock = {};
          ranges.forEach((r) => {
            (byBlock[r.blockKey] = byBlock[r.blockKey] || []).push(r);
          });

          Object.entries(byBlock).forEach(([blockKey, arr]) => {
            const blockText = newContent.getBlockForKey(blockKey).getText();
            arr
              .sort((a, b) => b.start - a.start)
              .forEach(({ start, end }) => {
                const { start: origStart, end: origEnd } = resolveReplaceOffsets(
                  blockText,
                  start,
                  end,
                  ignoreAccents
                );
                const stylesAt = newContent
                  .getBlockForKey(blockKey)
                  .getInlineStyleAt(origStart);
                const selection = SelectionState.createEmpty(blockKey).merge({
                  anchorOffset: origStart,
                  focusOffset: origEnd,
                });
                newContent = Modifier.replaceText(
                  newContent,
                  selection,
                  replacement,
                  stylesAt
                );
              });
          });

          get().updateBox(boxId, (b) => {
            const columnToUpdate = b.content.columns.find((col) => col.key === columnKey);
            if (columnToUpdate) {
              columnToUpdate.title = JSON.stringify(convertToRaw(newContent));
            }
          });
          affectedBoxIds.push(boxId);
        } catch (e) {
          console.warn('Erreur lors du remplacement dans l\'en-tête:', e);
        }
      });

      // Traiter les cellules de données des tableaux
      const byTableCell = {};
      cellResults.forEach((r) => {
        const key = `${r.boxId}:${r.rowKey}:${r.columnKey}`;
        (byTableCell[key] = byTableCell[key] || []).push(r);
      });

      Object.entries(byTableCell).forEach(([key, ranges]) => {
        const [boxId, rowKey, columnKey] = key.split(':');
        const box = allBoxes.find((b) => b.id === boxId);
        if (!box || !isATableBox(box.type)) return;

        const row = box.content.data.find((r) => r.key === rowKey);
        if (!row || !row[columnKey]) return;

        try {
          const cellContent = row[columnKey];
          let editorState = transformTextToEditorState(cellContent);
          let newContent = editorState.getCurrentContent();

          const byBlock = {};
          ranges.forEach((r) => {
            (byBlock[r.blockKey] = byBlock[r.blockKey] || []).push(r);
          });

          Object.entries(byBlock).forEach(([blockKey, arr]) => {
            const blockText = newContent.getBlockForKey(blockKey).getText();
            arr
              .sort((a, b) => b.start - a.start)
              .forEach(({ start, end }) => {
                const { start: origStart, end: origEnd } = resolveReplaceOffsets(
                  blockText,
                  start,
                  end,
                  ignoreAccents
                );
                const stylesAt = newContent
                  .getBlockForKey(blockKey)
                  .getInlineStyleAt(origStart);
                const selection = SelectionState.createEmpty(blockKey).merge({
                  anchorOffset: origStart,
                  focusOffset: origEnd,
                });
                newContent = Modifier.replaceText(
                  newContent,
                  selection,
                  replacement,
                  stylesAt
                );
              });
          });

          get().updateBox(boxId, (b) => {
            const rowToUpdate = b.content.data.find((r) => r.key === rowKey);
            if (rowToUpdate) {
              rowToUpdate[columnKey] = JSON.stringify(convertToRaw(newContent));
            }
          });
          affectedBoxIds.push(boxId);
        } catch (e) {
          console.warn('Erreur lors du remplacement dans la cellule:', e);
        }
      });

      // Traiter les boxes de texte et titres
      const byBox = {};
      textResults.forEach((r) => {
        (byBox[r.boxId] = byBox[r.boxId] || []).push(r);
      });
      Object.entries(byBox).forEach(([boxId, ranges]) => {
        const box = allBoxes.find((b) => b.id === boxId);
        if (!box?.content?.editorState) return;
        let newContent = box.content.editorState.getCurrentContent();
        const byBlock = {};
        ranges.forEach((r) => {
          (byBlock[r.blockKey] = byBlock[r.blockKey] || []).push(r);
        });
        Object.entries(byBlock).forEach(([blockKey, arr]) => {
          const blockText = newContent.getBlockForKey(blockKey).getText();
          arr
            .sort((a, b) => b.start - a.start)
            .forEach(({ start, end }) => {
              const { start: origStart, end: origEnd } = resolveReplaceOffsets(
                blockText,
                start,
                end,
                ignoreAccents
              );
              const stylesAt = newContent
                .getBlockForKey(blockKey)
                .getInlineStyleAt(origStart);
              const selection = SelectionState.createEmpty(blockKey).merge({
                anchorOffset: origStart,
                focusOffset: origEnd,
              });
              newContent = Modifier.replaceText(
                newContent,
                selection,
                replacement,
                stylesAt
              );
            });
        });
        get().updateBox(boxId, (b) => {
          b.content.editorState = createEditorStateWithDecorator(newContent);
        });
        affectedBoxIds.push(boxId);
      });

      finalizeDocumentReplace(get, affectedBoxIds);
    }
    get().computeSearchResults();
    set({ searchSelectionActive: false }, false, 'replaceAll:document:done');
  },
});
